import { PairingToken } from './PairingToken';
import { MCPBridgeMessageHandler } from './MCPBridgeMessageHandler';
import { MCPBridgeSession } from './MCPBridgeSession';
import { MCPBridgeLogger, type MCPBridgeLoggerLike } from './MCPBridgeLogger';
import { toSafeString } from './logString';

const DEFAULT_URL = 'ws://127.0.0.1:45732/balus';

export class MCPBridgeConnection {
  private ws: WebSocket | null = null;
  private readonly session: MCPBridgeSession;
  private readonly handler: MCPBridgeMessageHandler;
  private readonly logger: MCPBridgeLoggerLike;

  constructor(session: MCPBridgeSession, handler: MCPBridgeMessageHandler, logger: MCPBridgeLoggerLike) {
    this.session = session;
    this.handler = handler;
    this.logger = logger;
  }

  connect(url: string = DEFAULT_URL): void {
    if (this.ws !== null) {
      this.ws.close();
      this.ws = null;
    }

    this.session.setConnecting();

    let token: PairingToken;
    let ws: WebSocket;
    try {
      token = PairingToken.generate();
      ws = new WebSocket(url, [token.toWireFormat()]);
    } catch (e) {
      this.logger.error('failed to connect', e);
      // トークン生成または WebSocket 生成失敗時はステータスを巻き戻す。
      // String(e) は throw Symbol() のような値で例外を投げ、接続失敗処理自体がクラッシュするため
      // toSafeString で安全に文字列化する。
      // 空文字になった場合は「reason 付き切断」ではなく「reason なし切断」として扱う。
      const reason = toSafeString(e);
      if (reason === '') {
        this.session.setDisconnected();
      } else {
        this.session.setDisconnected(reason);
      }
      return;
    }

    this.ws = ws;

    ws.onopen = () => {
      // ws はクロージャに固定されるため、this.ws と比較することで古いソケットのイベントを無視できる
      if (this.ws !== ws) return;
      this.logger.success(`Connected (token: ${token.toMaskedString()})`);
      this.session.setConnected();
    };
    ws.onmessage = (event) => {
      if (this.ws !== ws) return;
      if (ws.readyState !== WebSocket.OPEN) {
        this.logger.warn(`message dropped: socket not open ${ws.readyState}`);
        return;
      }
      this.handler
        .handle(event, (data) => {
          if (this.ws !== ws) {
            this.logger.warn('response dropped: socket replaced during handle');
            // TODO: requestModeling の実体を実装する際、peer のリトライにより同一 id の
            //   リクエストが再送される可能性があるため、id ベースの冪等性が必要。
            //   現状は dummy adapter が no-op のため副作用なし。実 adapter が wire-up
            //   された時点で「副作用は実行済みでレスポンスのみ握り潰す」window が開く点に注意。
            return { sent: false, reason: 'socket_replaced' };
          }
          if (ws.readyState !== WebSocket.OPEN) {
            this.logger.warn(`response dropped: socket not open ${ws.readyState}`);
            return { sent: false, reason: `socket_not_open:${ws.readyState}` };
          }
          // ws.send() は readyState === OPEN でも同期例外を投げうる（buffer 上限超過など）。
          // 例外を素通しさせると外側の .catch((e) => logger.error('handle error', e)) に
          // 落ちて SendResult 契約が破れるため、ここで捕捉して drop 経路に閉じる。
          try {
            ws.send(data);
          } catch (e) {
            const reason = toSafeString(e);
            this.logger.warn(`response dropped: send threw ${reason}`);
            return { sent: false, reason: `send_threw:${reason}` };
          }
          return { sent: true };
        })
        .catch((e) => {
          this.logger.error('handle error', e);
        });
    };
    ws.onclose = (event) => {
      // disconnect() による自発切断では、先に this.ws = null にしてあるため、
      // このガードが成立して握り潰される（reason 付き「異常切断」への上書きを防ぐ）。
      // サーバー起因の切断では this.ws === ws のままなのでガードを通過し、reason 付きで記録される。
      if (this.ws !== ws) return;
      this.logger.info(`closed ${event.code} ${event.reason}`);
      this.session.setDisconnected(`${event.code}: ${event.reason}`);
    };
  }

  /**
   * 接続を自発的に切断する。
   *
   * 一見すると `this.ws?.close()` の一行で済みそうだが、それだと「自発的な切断」と
   * 「サーバー起因の異常切断」を区別できなくなるため、以下の順序で実装している。
   *
   *   1. ローカル変数 ws に退避し、先に this.ws = null にする。
   *   2. setDisconnected() で reason なしの切断状態を記録する。
   *   3. ws.close() でソケットを閉じる。
   *
   * なぜこの順序が必須か：
   *   ws.close() を呼ぶと WebSocket は非同期で onclose イベントを発火させる。
   *   connect() 内で登録された onclose ハンドラは冒頭で
   *   `if (this.ws !== ws) return;` というガードを持つ。
   *   このガードは「クロージャに固定されたソケット ws」と「現在のインスタンス変数 this.ws」を
   *   比較することで、古いソケットや自発切断によるイベントを無視する仕組みになっている。
   *
   *   先に this.ws = null にすることでガード条件が成立し、onclose 内の
   *   `setDisconnected(`${code}: ${reason}`)` （reason 付き）が握り潰される。
   *   結果として「reason なし＝自発切断」の状態が保持され、UIはユーザー操作による
   *   正常な切断として扱える。
   *
   * もし this.ws?.close() と書いた場合：
   *   this.ws が当該 ws のまま close() が走るため、onclose のガードを通過してしまう。
   *   すると reason なしの「自発切断」状態が、onclose から呼ばれる reason 付きの
   *   「異常切断」状態へ上書きされ、自発切断とサーバー起因切断の区別が壊れる。
   */
  disconnect(): void {
    if (this.ws === null) return;
    const ws = this.ws;
    // 先に null にすることで、この後の ws.close() が誘発する onclose のガードを成立させる。
    // onclose 内の reason 付き setDisconnected() が握り潰され、自発切断の状態が保たれる。
    this.ws = null;
    this.session.setDisconnected();
    ws.close();
  }
}

export function createMCPBridgeConnection(): MCPBridgeConnection {
  const logger = new MCPBridgeLogger();
  return new MCPBridgeConnection(new MCPBridgeSession(), new MCPBridgeMessageHandler(logger), logger);
}
